import { useState, useEffect } from 'react'
import styles from './ProjectTaskSelectionModal.module.css'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function ProjectTaskSelectionModal({
    isOpen,
    onClose,
    onSelection,
    availableProjects = [],
    availableTasks = [],
    initialSelectedProject = null,
    initialSelectedTask = null
}) {
    const [selectedProject, setSelectedProject] = useState(null)
    const [selectedTask, setSelectedTask] = useState(null)
    const [filteredTasks, setFilteredTasks] = useState([])
    const [isBillable, setIsBillable] = useState(false)

    // Add escape key functionality
    useEscapeKey(isOpen, onClose)

    useEffect(() => {
        if (isOpen) {
            setSelectedProject(initialSelectedProject)
            setSelectedTask(initialSelectedTask)
            // Auto-set billable based on initial selections
            if (initialSelectedTask) {
                setIsBillable(initialSelectedTask.is_billable ?? initialSelectedProject?.is_billable ?? true)
            } else if (initialSelectedProject) {
                setIsBillable(initialSelectedProject.is_billable ?? true)
            } else {
                setIsBillable(false)
            }
        }
    }, [isOpen, initialSelectedProject, initialSelectedTask])

    useEffect(() => {
        if (selectedProject) {
            const projectTasks = availableTasks.filter(task => task.project_id === selectedProject.id)
            setFilteredTasks(projectTasks)
            
            if (selectedTask && selectedTask.project_id !== selectedProject.id) {
                setSelectedTask(null)
            }
        } else {
            setFilteredTasks([])
            setSelectedTask(null)
        }
    }, [selectedProject, availableTasks])

    const handleProjectChange = (e) => {
        const projectId = e.target.value
        if (projectId === '') {
            setSelectedProject(null)
            // Reset billable to project default or false
            setIsBillable(false)
        } else {
            const project = availableProjects.find(p => p.id === projectId)
            setSelectedProject(project)
            // Set billable based on project's is_billable field
            setIsBillable(project?.is_billable ?? true)
        }
    }

    const handleTaskChange = (e) => {
        const taskId = e.target.value
        if (taskId === '') {
            setSelectedTask(null)
            // When task is cleared, fall back to project's billable status
            setIsBillable(selectedProject?.is_billable ?? true)
        } else {
            const task = filteredTasks.find(t => t.id === taskId)
            setSelectedTask(task)
            // Set billable based on task's is_billable field (task overrides project)
            if (task) {
                setIsBillable(task.is_billable ?? selectedProject?.is_billable ?? true)
            }
        }
    }

    const handleConfirmSelection = () => {
        onSelection({
            project: selectedProject,
            task: selectedTask,
            billable: isBillable
        })
        onClose()
    }

    const handleClear = () => {
        setSelectedProject(null)
        setSelectedTask(null)
        setIsBillable(false)
    }

    const handleCancel = () => {
        onClose()
    }

    const getDisplayText = () => {
        if (selectedProject && selectedTask) {
            return `${selectedProject.name} > ${selectedTask.name}`
        } else if (selectedProject) {
            return selectedProject.name
        }
        return 'No selection'
    }

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Select Project & Task</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.selectionPreview}>
                        <span className={styles.previewLabel}>Current Selection:</span>
                        <span className={styles.previewText}>{getDisplayText()}</span>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.formGroup}>
                            <label htmlFor="projectSelect" className={styles.label}>
                                Project
                            </label>
                            <select
                                id="projectSelect"
                                className={styles.select}
                                value={selectedProject?.id || ''}
                                onChange={handleProjectChange}
                            >
                                <option value="">Select a project</option>
                                {availableProjects.map(project => (
                                    <option key={project.id} value={project.id}>
                                        {project.name}
                                        {project.client_name && ` (${project.client_name})`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="taskSelect" className={styles.label}>
                                Task (Optional)
                            </label>
                            <select
                                id="taskSelect"
                                className={styles.select}
                                value={selectedTask?.id || ''}
                                onChange={handleTaskChange}
                                disabled={!selectedProject || filteredTasks.length === 0}
                            >
                                <option value="">Select a task</option>
                                {filteredTasks.map(task => (
                                    <option key={task.id} value={task.id}>
                                        {task.name}
                                    </option>
                                ))}
                            </select>
                            {selectedProject && filteredTasks.length === 0 && (
                                <div className={styles.noTasksMessage}>
                                    No tasks available for this project
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button
                        type="button"
                        className={styles.clearButton}
                        onClick={handleClear}
                        disabled={!selectedProject && !selectedTask}
                    >
                        Clear Selection
                    </button>
                    <div className={styles.rightButtons}>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.confirmButton}
                            onClick={handleConfirmSelection}
                            disabled={!selectedProject}
                        >
                            Confirm Selection
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}